/* AdminBusinessOrders - Overzicht van zakelijke bestellingen (aanvragen ter controle).
   Updated: Initial creation - admin can view and manage business order requests. */

import { useEffect, useState, useCallback } from 'react';
import Breadcrumb from '@/components/admin/Breadcrumb';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Package, Eye, Loader as Loader2, Truck, Store, Calendar, Building2,
} from 'lucide-react';
import { toast } from 'sonner';

interface BusinessOrderItem {
  product_name: string;
  quantity: string;
  notes: string;
}

interface BusinessOrder {
  id: string;
  order_number: string;
  business_customer_id: string;
  items: BusinessOrderItem[];
  delivery_method: string;
  desired_delivery_date: string | null;
  pickup_date: string | null;
  notes: string | null;
  status: string;
  created_at: string;
  business_customers?: {
    company_name: string;
    contact_first_name: string;
    contact_last_name: string;
    email: string;
    phone: string;
    city: string;
    kvk_number: string;
  };
}

const STATUS_LABELS: Record<string, string> = {
  new: 'Nieuw',
  in_review: 'In behandeling',
  confirmed: 'Bevestigd',
  cancelled: 'Geannuleerd',
};

const STATUS_COLORS: Record<string, string> = {
  new: 'bg-blue-100 text-blue-800 border-blue-200',
  in_review: 'bg-amber-100 text-amber-800 border-amber-200',
  confirmed: 'bg-green-100 text-green-800 border-green-200',
  cancelled: 'bg-red-100 text-red-800 border-red-200',
};

export default function AdminBusinessOrders() {
  const [orders, setOrders] = useState<BusinessOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<BusinessOrder | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updating, setUpdating] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('business_orders')
        .select(`
          *,
          business_customers (
            company_name,
            contact_first_name,
            contact_last_name,
            email,
            phone,
            city,
            kvk_number
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setOrders((data || []) as BusinessOrder[]);
    } catch {
      toast.error('Kon bestellingen niet ophalen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const filtered = orders.filter(o => statusFilter === 'all' || o.status === statusFilter);

  const updateStatus = async (orderId: string, newStatus: string) => {
    setUpdating(true);
    try {
      const { error } = await supabase
        .from('business_orders')
        .update({ status: newStatus })
        .eq('id', orderId);

      if (error) throw error;

      toast.success('Status bijgewerkt');
      fetchOrders();
      if (selectedOrder?.id === orderId) {
        setSelectedOrder(prev => prev ? { ...prev, status: newStatus } : null);
      }
    } catch {
      toast.error('Kon status niet bijwerken');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('nl-NL', {
    day: 'numeric', month: 'short', year: 'numeric',
  });

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <Breadcrumb items={[{ label: 'Zakelijk', path: '/kh-admin/zakelijk/bestellingen' }, { label: 'Bestellingen' }]} />
      <h1 className="text-4xl font-bold text-[#1e2b22] mb-2">Zakelijke bestellingen</h1>
      <p className="text-[#5a6552] mb-8">Overzicht van bestellingen (aanvragen ter controle) van zakelijke klanten</p>

      {/* Filter */}
      <div className="mb-6">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 px-3 rounded-md border border-[#e7e1d6] bg-white text-[#1e2b22] text-sm focus:border-[#1e2b22]"
        >
          <option value="all">Alle statussen</option>
          <option value="new">Nieuw</option>
          <option value="in_review">In behandeling</option>
          <option value="confirmed">Bevestigd</option>
          <option value="cancelled">Geannuleerd</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-[#e7e1d6] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#5a6552]">
            <Loader2 className="h-6 w-6 animate-spin mx-auto" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <Package className="h-10 w-10 text-[#cfcac2] mx-auto mb-3" />
            <p className="text-[#5a6552]">Geen zakelijke bestellingen gevonden</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#fcfaf6] border-b border-[#e7e1d6]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">Ordernummer</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">Bedrijf</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">Producten</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">Bezorging</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-[#5a6552] uppercase tracking-wider">Datum</th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-[#5a6552] uppercase tracking-wider">Actie</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e7e1d6]">
                {filtered.map((order) => (
                  <tr key={order.id} className="hover:bg-[#e7e1d6]/30 transition-colors">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-[#1e2b22]">{order.order_number}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-[#1e2b22]">{order.business_customers?.company_name || '-'}</p>
                      <p className="text-xs text-[#5a6552]">{order.business_customers?.email}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#5a6552]">{order.items.length} product(en)</p>
                      <p className="text-xs text-[#5a6552]">
                        {order.items.slice(0, 2).map(i => i.product_name).join(', ')}
                        {order.items.length > 2 ? '...' : ''}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-[#5a6552] flex items-center gap-1">
                        {order.delivery_method === 'bezorgen' ? <Truck className="h-3.5 w-3.5" /> : <Store className="h-3.5 w-3.5" />}
                        {order.delivery_method === 'bezorgen' ? 'Bezorgen' : 'Afhalen'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[order.status]}`}>
                        {STATUS_LABELS[order.status]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-[#5a6552]">{formatDate(order.created_at)}</p>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedOrder(order)}
                        className="text-[#5a6552] hover:bg-[#e7e1d6]/50"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail dialog */}
      {selectedOrder && (
        <Dialog open={!!selectedOrder} onOpenChange={(open) => { if (!open) setSelectedOrder(null); }}>
          <DialogContent className="max-w-2xl border-[#e7e1d6] bg-[#fcfaf6]">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#1e2b22] flex items-center gap-2">
                <Package className="h-5 w-5 text-[#5a6552]" />
                {selectedOrder.order_number}
              </DialogTitle>
            </DialogHeader>

            {/* Customer info */}
            <div className="bg-white rounded-xl border border-[#e7e1d6] p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="h-4 w-4 text-[#5a6552]" />
                <p className="font-medium text-[#1e2b22] text-sm">{selectedOrder.business_customers?.company_name}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm text-[#5a6552]">
                <p>{selectedOrder.business_customers?.contact_first_name} {selectedOrder.business_customers?.contact_last_name}</p>
                <p>{selectedOrder.business_customers?.email}</p>
                <p>{selectedOrder.business_customers?.phone}</p>
                <p>KVK: {selectedOrder.business_customers?.kvk_number}</p>
              </div>
            </div>

            {/* Order items */}
            <div className="bg-white rounded-xl border border-[#e7e1d6] p-4 mb-4">
              <p className="text-sm font-medium text-[#1e2b22] mb-3">Producten</p>
              <div className="space-y-2">
                {selectedOrder.items.map((item, i) => (
                  <div key={i} className="flex justify-between items-start py-2 border-b border-[#e7e1d6] last:border-0">
                    <div>
                      <p className="text-sm text-[#1e2b22] font-medium">{item.product_name}</p>
                      {item.notes && <p className="text-xs text-[#5a6552] mt-0.5">{item.notes}</p>}
                    </div>
                    <p className="text-sm font-medium text-[#1e2b22]">{item.quantity}x</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Delivery info */}
            <div className="bg-white rounded-xl border border-[#e7e1d6] p-4 mb-4">
              <p className="text-sm font-medium text-[#1e2b22] mb-2">Bezorging</p>
              <div className="flex flex-wrap gap-4 text-sm text-[#5a6552]">
                <span className="flex items-center gap-1">
                  {selectedOrder.delivery_method === 'bezorgen' ? <Truck className="h-4 w-4" /> : <Store className="h-4 w-4" />}
                  {selectedOrder.delivery_method === 'bezorgen' ? 'Bezorgen' : 'Afhalen in Dedemsvaart'}
                </span>
                {selectedOrder.desired_delivery_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Leverdatum: {formatDate(selectedOrder.desired_delivery_date)}
                  </span>
                )}
                {selectedOrder.pickup_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Afhaaldatum: {formatDate(selectedOrder.pickup_date)}
                  </span>
                )}
              </div>
              {selectedOrder.notes && (
                <div className="mt-3 pt-3 border-t border-[#e7e1d6]">
                  <p className="text-xs text-[#5a6552] uppercase tracking-wide mb-1">Opmerking</p>
                  <p className="text-sm text-[#1e2b22]">{selectedOrder.notes}</p>
                </div>
              )}
            </div>

            {/* Status actions */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#5a6552] mr-2">Status:</span>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${STATUS_COLORS[selectedOrder.status]}`}>
                {STATUS_LABELS[selectedOrder.status]}
              </span>
            </div>

            <DialogFooter className="flex-wrap gap-2">
              {selectedOrder.status === 'new' && (
                <Button
                  onClick={() => updateStatus(selectedOrder.id, 'in_review')}
                  disabled={updating}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                >
                  In behandeling nemen
                </Button>
              )}
              {(selectedOrder.status === 'new' || selectedOrder.status === 'in_review') && (
                <Button
                  onClick={() => updateStatus(selectedOrder.id, 'confirmed')}
                  disabled={updating}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Bevestigen
                </Button>
              )}
              {selectedOrder.status !== 'cancelled' && selectedOrder.status !== 'confirmed' && (
                <Button
                  onClick={() => updateStatus(selectedOrder.id, 'cancelled')}
                  disabled={updating}
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50"
                >
                  Annuleren
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => setSelectedOrder(null)}
                className="border-[#e7e1d6] text-[#5a6552]"
              >
                Sluiten
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
